/**
 * Classifies a request by which cached inputs of an access decision its success invalidates.
 *
 * `useAccessControl` gates every CTA on three caches that nothing tells when the server has moved
 * on. Each mutation site could invalidate its own, but that is the contract that got missed on most
 * pages, so the transport does it instead (see the interceptors in `axiosInstance`).
 *
 * Err towards flagging: a false positive costs one extra read, a false negative is the bug this
 * exists to prevent. The tables below only exclude endpoints where that read would be pure waste.
 */

export type AccessSurface = 'quota' | 'grants' | 'plan'

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete'])
const CREATE_OR_DELETE_METHODS = new Set(['post', 'delete'])
const UPDATE_METHODS = new Set(['put', 'patch'])

/** Resource paths whose create/delete traffic changes what `/metrics` reports as consumed. */
const QUOTA_RESOURCE_PREFIXES = [
	'/v3/action-types',
	'/v3/actions',
	'/v3/apikeys',
	'/v3/dashboards',
	'/v3/dbcs',
	'/v3/devices',
	'/v3/exporters',
	'/v3/metadata-keys',
	'/v3/roles',
	'/v3/session-rules',
	'/v3/streams',
	'/v3/subscriber-groups',
	'/v3/users',
]

/** Owners count against Users, project lifecycle against Projects, device attach against Devices. */
const QUOTA_RESOURCE_PATTERNS = [/^\/v3\/organizations\/[^/]+\/(owners|tenants|devices)(\/|$)/]

/** Collections whose POST is a filtered list rather than a create. */
const LIST_OVER_POST_PATHS = ['/v3/session-rules', '/v3/subscriber-groups']

/** Paths under a quota resource that read, export, or dry-run instead of changing a count. */
const NON_COUNTING_PREFIXES = [
	'/v3/actions/latency-histogram',
	'/v3/actions/mark-as-completed',
	'/v3/dbcs/dashboard',
	'/v3/devices/configurations/download',
	'/v3/devices/download',
	'/v3/devices/filter-options',
	'/v3/devices/ids',
	'/v3/devices/metadata-bulk',
	'/v3/devices/search',
	'/v3/session-rules/test',
	'/v3/streams/filter-options',
	'/v3/streams/search',
	'/v3/subscriber-groups/test',
]

/** State transitions and per-resource reads that leave the counter alone. */
const NON_COUNTING_PATTERNS = [
	/^\/v3\/actions\/[^/]+\/(mark-as-completed|devices)$/,
	/^\/v3\/dashboards\/[^/]+\/(share|rollback)(\/|$)/,
	/^\/v3\/dbcs\/batch\/(start|stop)$/,
	/^\/v3\/subscriber-groups\/[^/]+\/(enable|disable)$/,
	/^\/v3\/organizations\/[^/]+\/devices\/(search|filter-options|ids)$/,
]

/** `/api/v3/users?x=1` and `v3/users` both normalize to `/v3/users`. */
const normalizePath = (url: string): string => {
	const withoutQuery = url.split('?')[0].split('#')[0]
	const absolute = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`
	return absolute.startsWith('/api/') ? absolute.slice('/api'.length) : absolute
}

/** Prefix match on a path-segment boundary, so `/v3/devices` never matches `/v3/device-configs`. */
const matchesPrefix = (path: string, prefix: string): boolean => path === prefix || path.startsWith(`${prefix}/`)

const matches = (path: string, prefixes: string[] = [], patterns: RegExp[] = []): boolean =>
	prefixes.some(prefix => matchesPrefix(path, prefix)) || patterns.some(pattern => pattern.test(path))

const changesACount = (method: string, path: string): boolean => {
	if (CREATE_OR_DELETE_METHODS.has(method)) {
		// The exclusion is about POST being a list call on those collections. A DELETE addressed to
		// the same path is still a delete, the way user deletion is DELETE on the bare /v3/users.
		return method !== 'post' || !LIST_OVER_POST_PATHS.includes(path)
	}
	// `PUT /v3/subscriber-groups/batch/delete` is a delete despite the method.
	return UPDATE_METHODS.has(method) && path.endsWith('/delete')
}

/** Creating some other user cannot change the caller's grants, so `/v3/users` counts only on reassignment. */
const GRANTS_PREFIXES = ['/v3/roles']
const GRANTS_REASSIGNMENT_PREFIXES = ['/v3/users']
const GRANTS_PATTERNS = [/^\/v3\/organizations\/[^/]+\/owners(\/|$)/]

/** Plan changes go through the console client, which is the only one that reaches billing. */
const PLAN_PATTERNS = [/^\/v1\/organizations\/[^/]+\/(subscribe|modify)$/]

const affectsQuota = (method: string, path: string): boolean => {
	if (!changesACount(method, path)) {
		return false
	}

	return (
		matches(path, QUOTA_RESOURCE_PREFIXES, QUOTA_RESOURCE_PATTERNS) &&
		!matches(path, NON_COUNTING_PREFIXES, NON_COUNTING_PATTERNS)
	)
}

const affectsGrants = (method: string, path: string): boolean => {
	if (GRANTS_PREFIXES.some(prefix => matchesPrefix(path, prefix))) {
		return true
	}
	if (GRANTS_PATTERNS.some(pattern => pattern.test(path))) {
		return method === 'post' || method === 'delete'
	}
	return (
		(method === 'patch' || method === 'put') && GRANTS_REASSIGNMENT_PREFIXES.some(prefix => matchesPrefix(path, prefix))
	)
}

const affectsPlan = (method: string, path: string): boolean => method === 'post' && matches(path, [], PLAN_PATTERNS)

/** The access inputs a successful request invalidates. Empty when it invalidates nothing. */
export const accessSurfacesAffectedBy = (method: string | undefined, url: string | undefined): AccessSurface[] => {
	if (!method || !url) {
		return []
	}

	const verb = method.toLowerCase()
	if (!MUTATING_METHODS.has(verb)) {
		return []
	}

	const path = normalizePath(url)
	const surfaces: AccessSurface[] = []
	if (affectsQuota(verb, path)) {
		surfaces.push('quota')
	}
	if (affectsGrants(verb, path)) {
		surfaces.push('grants')
	}
	if (affectsPlan(verb, path)) {
		surfaces.push('plan')
	}
	return surfaces
}
